<template>
  <div class="kl-summary">
    <div class="kl-summary-title">
      {{ $t("teaching.instructional.summaryTitle") }}
    </div>

    <div class="table-responsive mb-0">
      <table class="table table-sm mb-0 kl-summary-table">
        <thead>
          <tr>
            <th>{{ $t("teaching.instructional.activityMomentsField") }}</th>
            <th class="text-center">
              {{ $t("teaching.instructional.summaryWeek") }}
            </th>
            <!-- E24: la tabla ya no lista solo actividades. La columna nombra
                 las dos cosas porque las dos ocupan una fila. -->
            <th>{{ activityColumnLabel }}</th>
            <!-- Pegada al nombre: la ponderacion se lee como un atributo de lo que
                 pondera, no como una columna suelta al final de la fila. -->
            <th class="text-center">
              {{ $t("teaching.instructional.summaryWeighing") }}
            </th>
            <th>
              {{
                $getVisibleNames(
                  "teaching.studyunit",
                  true,
                  "Unidades de Estudio"
                )
              }}
            </th>
            <th class="text-center">
              {{ $t("teaching.instructional.summaryIndicators") }}
            </th>
            <th class="text-center">
              {{ $t("teaching.instructional.summaryStatus") }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.id">
            <td>
              <!-- E30 · Una actividad cumple varios momentos: se listan todos. -->
              <span
                v-for="moment in row.moments"
                :key="`${row.id}-moment-${moment.id}`"
                class="kl-chip kl-moment-chip"
                :style="momentChipStyle(moment)"
              >
                {{ moment.name }}
              </span>
              <span v-if="row.moments.length === 0" class="text-muted small">
                {{ $t("teaching.instructional.momentNone") }}
              </span>
            </td>
            <td class="text-center">
              <span
                v-if="row.week_label"
                :class="{ 'kl-week-overflow': row.week_overflow }"
              >
                {{ row.week_label }}
              </span>
              <span v-else class="text-muted">-</span>
            </td>
            <td>
              <!-- E24: el tipo delante del nombre, igual que en la tarjeta: es lo
                   que distingue una evaluacion referenciada de una Actividad. -->
              <span v-if="row.type_name" class="kl-chip kl-chip-eval">
                {{ row.type_name }}
              </span>
              <strong>{{ row.title }}</strong>
            </td>
            <!-- Se ve de un vistazo que pondera y que no, y cuanto suma el
                 diseno entero. Sin ponderacion va un guion, no un 0: una
                 Formativa no vale 0%, no pondera. -->
            <td class="text-center">
              <span v-if="row.weighing" class="kl-weighing-cell">
                {{ row.weighing }}%
              </span>
              <span v-else class="text-muted">-</span>
            </td>
            <td>
              <template v-if="row.study_units.length > 0">
                <span
                  v-for="study_unit in row.study_units"
                  :key="`${row.id}-su-${study_unit.id}`"
                  class="kl-chip"
                  :title="study_unit.name"
                >
                  {{ $t("reusable.common.numberAbbreviation")
                  }}{{ getCompetenceUnitOrder(study_unit.temp_competence_unit)
                  }}{{ study_unit.order }}
                </span>
              </template>
              <span v-else class="text-muted small">
                {{ $t("teaching.instructional.summaryNoStudyUnits", {
                  entity: studyUnitEntityNamePlural,
                }) }}
              </span>
            </td>
            <td class="text-center">{{ row.indicators_count }}</td>
            <td class="text-center">
              <InstructionalStatusBadge
                :status="row.status"
              ></InstructionalStatusBadge>
            </td>
          </tr>
          <tr v-if="rows.length === 0">
            <td colspan="8" class="text-center text-muted py-3">
              {{ $t("teaching.instructional.summaryEmpty", {
            entity: piecesEntityNamePlural,
          }) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import * as names from "@/store/names";
// E37 · El tipo 4 es la "Actividad", la pieza propia del diseño.
import { WRAPPER_TYPE_ACTIVITY } from "@/utils/instructional_activity_types";

export default {
  name: "InstructionalSummaryTable",
  data() {
    return { WRAPPER_TYPE_ACTIVITY: WRAPPER_TYPE_ACTIVITY };
  },
  components: {
    InstructionalStatusBadge: () => import("./InstructionalStatusBadge"),
  },
  props: {
    // Modulos del diseño, en orden.
    modules: {
      type: Array,
      default: () => [],
    },
    // Actividades del diseño. La unidad de lectura de la tabla es la actividad.
    activities: {
      type: Array,
      default: () => [],
    },
    // Momentos del método activo, en orden.
    moments: {
      type: Array,
      default: () => [],
    },
    // Semana calculada de cada actividad: Map(activity_id -> {from,to,overflow}).
    activity_weeks: {
      type: Map,
      default: () => new Map(),
    },
  },
  computed: {
    piecesEntityNamePlural() {
      return `${this.$getVisibleNames(
        "manual.instructional_design.activity",
        true,
        "Actividades"
      )}/${this.$getVisibleNames(
        "evaluations2.evaluation",
        true,
        "Evaluaciones"
      )}`;
    },
    studyUnitEntityNamePlural() {
      return this.$getVisibleNames(
        "teaching.studyunit",
        true,
        "Unidades de Estudio"
      );
    },
    ...mapGetters({
      getStudyUnit: names.STUDY_UNIT,
      competence_units: names.COMPETENCE_UNITS,
      evaluationTypes: names.EPM_EVALUATION_TYPES,
      indicators: names.EVALUATION_CRITERIAS,
    }),
    containerEntityName() {
      return this.$getVisibleNames(
        "manual.instructional_design.container",
        false,
        "Módulo"
      );
    },
    activityEntityName() {
      return this.$getVisibleNames(
        "manual.instructional_design.activity",
        false,
        "Actividad"
      );
    },
    // E24: en la tabla conviven las Actividades del DI y las evaluaciones del
    // Programa, asi que la columna nombra a las dos.
    activityColumnLabel() {
      return this.$t("teaching.instructional.summaryActivityOrEvaluation", {
        activity: this.$getVisibleNames(
          "manual.instructional_design.activity",
          true,
          "Actividades"
        ),
        evaluation: this.$getVisibleNames(
          "evaluations2.evaluation",
          true,
          "Evaluaciones"
        ),
      });
    },
    // E37 · Índice IL → su Unidad de Estudio, para derivar los RA de cada fila.
    indicatorById() {
      return new Map(this.indicators.map((x) => [x.id, x]));
    },
    // Una fila por actividad, agrupadas por modulo y ordenadas por momento:
    // la tabla espeja la estructura de la vista.
    rows() {
      const module_order = new Map(
        this.modules.map((module, index) => [module.id, index])
      );
      const last_module = this.modules.length;
      return [...this.activities]
        .sort((a, b) => {
          const a_module = module_order.has(a.module)
            ? module_order.get(a.module)
            : last_module;
          const b_module = module_order.has(b.module)
            ? module_order.get(b.module)
            : last_module;
          if (a_module != b_module) return a_module - b_module;
          // E30 · Una sola secuencia por Modulo: el momento ya no ordena.
          return a.order - b.order || a.id - b.id;
        })
        .map((activity) => {
          const activity_moments = (activity.moments || [])
            .map((moment_id) => this.moments.find((x) => x.id == moment_id))
            .filter((moment) => moment != null)
            .sort((a, b) => a.order - b.order);
          const module = this.modules.find((x) => x.id == activity.module);
          // E37 · Los RA se derivan de los IL: el wrapper no guarda `study_units`.
          const study_unit_ids = new Set(
            (activity.evaluation_criterias_micro || [])
              .map((id) => (this.indicatorById.get(id) || {}).study_unit)
              .filter((x) => x != null)
          );
          const study_units = [...study_unit_ids]
            .map((study_unit_id) => this.getStudyUnit(study_unit_id))
            .filter((study_unit) => study_unit != null)
            .sort((a, b) => a.order - b.order);

          // E37 · La fila ES la evaluación del Programa: nombre, IL y ponderación salen de
          // ella sin ramas. El tipo se muestra en todas menos en la Actividad (tipo 4), que
          // es la pieza propia del diseño y no necesita distintivo.
          const is_activity = activity.type == this.WRAPPER_TYPE_ACTIVITY;
          const weighing = this.weighingLabel(activity);
          const type = this.evaluationTypes.find((x) => x.id == activity.type);

          return {
            id: activity.id,
            title:
              activity.name ||
              this.$t("teaching.instructional.activityUntitled", {
                entity: this.activityEntityName,
              }),
            type_name: is_activity || !type ? null : type.value,
            module_title: module
              ? module.title ||
                this.$t("teaching.instructional.moduleUntitled", {
                  entity: this.containerEntityName,
                })
              : this.$t("teaching.instructional.activityNoModule", {
                  entity: this.containerEntityName,
                }),
            has_module: module != null,
            moments: activity_moments,
            status: activity.instructional_status,
            week_label: this.weekLabel(activity.id),
            week_overflow: (this.activity_weeks.get(activity.id) || {}).overflow,
            study_units: study_units,
            indicators_count: (activity.evaluation_criterias_micro || [])
              .length,
            // La ponderacion vive en el wrapper del Programa en los dos casos, y
            // el serializer la expone derivada. `null` cuando no pondera.
            weighing: weighing,
          };
        });
    },
  },
  methods: {
    // Mismo criterio que la tarjeta: el texto se aclara u oscurece según el color.
    momentChipStyle(moment) {
      const background_color =
        moment.color && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(moment.color)
          ? moment.color
          : "#2361a0";
      return {
        backgroundColor: background_color,
        color: this.$lightOrDark(background_color) ? "#2b2a2a" : "#ffffff",
        borderColor: background_color,
      };
    },
    // La semana se deriva en el orquestador; acá sólo se rotula.
    weekLabel(activity_id) {
      const week = this.activity_weeks.get(activity_id);
      if (!week) return null;
      if (week.from == week.to)
        return this.$t("teaching.instructional.activityWeek", {
          week: week.from,
        });
      return this.$t("teaching.instructional.activityWeekRange", {
        from: week.from,
        to: week.to,
      });
    },
    // Se muestra solo si pondera de verdad: un 0 seria ruido, y la distincion
    // Formativa/Sumativa es justamente esa.
    weighingLabel(activity) {
      const weighing = Number(activity.weighing);
      if (isNaN(weighing) || weighing <= 0) return null;
      return parseFloat(weighing.toFixed(2));
    },
    // Prefijo de la Unidad de Competencia, cuando la institución las usa.
    getCompetenceUnitOrder(temp_competence_unit_id) {
      const competence_unit = this.competence_units.find(
        (x) => x.id == temp_competence_unit_id
      );
      return competence_unit ? competence_unit.order + "." : "";
    },
  },
};
</script>

<style scoped>
.kl-summary {
  background-color: #fff;
  border: 1px solid var(--kl-table-border, #d9e0e0);
  border-radius: var(--kl-radius-md, 8px);
  overflow: hidden;
  margin-bottom: 1rem;
}
/* Título de panel: el mismo en los tres bloques resumen del DI (este, el de uso de
   {indicator} y el Estado del Diseño). Es un "subtítulo / título de card" del estándar:
   --kl-fs-title-sm en 600. No se hereda ningún tamaño: el de este bloque salía del
   navegador y por eso los tres se veían distintos. */
.kl-summary-title {
  padding: 0.6rem 0.9rem;
  font-size: var(--kl-fs-title-sm);
  font-weight: var(--kl-fw-semibold, 600);
  text-transform: uppercase;
  color: var(--kl-text-on-primary, #fff);
  background-color: var(--kl-color-accent-medium) !important;
  border-bottom: 1px solid var(--kl-table-border, #d9e0e0);
}
/* Header estándar de tablas: azul medio + texto blanco. */
.kl-summary-table >>> thead th {
  background-color: var(--kl-color-accent-medium) !important;
  color: var(--kl-text-on-primary, #fff);
  font-weight: var(--kl-fw-bold, 700);
  border: 0;
  font-size: var(--kl-table-header-fs);
  text-transform: uppercase;
  letter-spacing: 0.03em;
  vertical-align: middle;
}
.kl-summary-table td {
  border-top: 1px solid var(--kl-table-border, #d9e0e0);
  vertical-align: middle;
  font-size: var(--kl-table-fs);
}
.kl-module-cell {
  font-size: var(--kl-fs-body);
  color: var(--kl-color-primary, #006ec8);
  font-weight: var(--kl-fw-bold, 700);
}
.kl-module-none {
  color: #97a5a3;
  font-style: italic;
  font-weight: var(--kl-fw-normal, 400);
}
.kl-moment-chip {
  font-size: var(--kl-fs-caption);
}
.kl-week-overflow {
  color: #a12d2d;
  font-weight: var(--kl-fw-bold, 700);
}
.kl-chip {
  display: inline-block;
  background-color: var(--kl-chip-bg, #e0f1ff);
  color: var(--kl-chip-text, #006ec8);
  border: 1px solid var(--kl-chip-border, #b6d9f5);
  border-radius: var(--kl-radius-pill, 999px);
  padding: var(--kl-chip-padding, 2px 8px);
  font-size: var(--kl-chip-fs);
  font-weight: var(--kl-chip-fw, 600);
  margin: 0.1rem 0.15rem 0.1rem 0;
}
.kl-weighing-cell {
  color: var(--kl-color-primary, #006ec8);
  font-weight: var(--kl-fw-bold, 700);
}
/* Distintivo del tipo de una evaluacion referenciada (E24). */
.kl-chip-eval {
  text-transform: uppercase;
  font-size: var(--kl-fs-caption);
  letter-spacing: 0.02em;
}
</style>
